import { __assign, __spreadArrays } from "tslib";
import { evaluateCondition, UnavailabilityReason, UnavailabileVariationReason, FulfillmentType, days, getDishOptionChoicesRange, daysOfWeekAsWeekday, } from '../index';
import { evaluateHierarchyConditions } from './evaluateHierarchyConditions';
import { extractLocalizedString } from './i18nLogic';
import { slugCreatorFactory } from './slugLogic';
import { calcCatalogMenuItemPriceRange, calcMenuItemPriceRange, } from './priceRangeLogic';
import { getDisplayablePrice, getErrorsOfChoice, isDishOptionHierarchyAvailable, isItemAvailable, } from './displayableOrderItem';
import { getPriceInCents, getSafeStringValue } from '../utils';
export function getItemsHash(menu) {
    var result = {};
    menu.items.forEach(function (item) {
        result[item.id] = item;
    });
    return result;
}
export function getCatalogItemsHash(items) {
    var result = {};
    items.forEach(function (item) {
        if (item.id) {
            result[item.id] = item;
        }
    });
    return result;
}
export function extractImageUrl(item, sectionMedia) {
    var _a;
    if ((_a = item.media) === null || _a === void 0 ? void 0 : _a.logo) {
        return item.media.logo.replace('https://static.wixstatic.com/media/', '');
    }
    else if (sectionMedia === null || sectionMedia === void 0 ? void 0 : sectionMedia.placeholder) {
        return sectionMedia.placeholder.replace('https://static.wixstatic.com/media/', '');
    }
    return undefined;
}
export function getDisplayableMenu(menu, locale, currency, dispatchTime, platform, dispatchType, mode) {
    if (mode === void 0) { mode = 'online-ordering'; }
    var itemsHash = getItemsHash(menu);
    var slugCreator = slugCreatorFactory();
    var result = [];
    var conditionParams = { dispatchTime: dispatchTime, platform: platform, dispatchType: dispatchType };
    var isAvailable = function (conditionResult) {
        return !conditionResult.reasons.find(function (reason) { return reason.type === 'order_delivery_time'; });
    };
    var isHidden = function (conditionResult) {
        return !conditionResult.result &&
            conditionResult.reasons.find(function (reason) { return reason.type === 'false'; });
    };
    var isIncorrectDispatchType = function (conditionResult, _mode) {
        return !conditionResult.result &&
            !!conditionResult.reasons.find(function (reason) { return reason.type === 'order_delivery_type' && _mode !== 'catalog'; });
    };
    var isIncorrectPlatformType = function (conditionResult, _mode) {
        return !conditionResult.result &&
            !!conditionResult.reasons.find(function (reason) { return reason.type === 'order_platform' && _mode !== 'catalog'; });
    };
    menu.sections.forEach(function (section) {
        var subMenuConditionResult = evaluateCondition(section.displayCondition, conditionParams);
        if (isHidden(subMenuConditionResult) ||
            isIncorrectDispatchType(subMenuConditionResult, mode) ||
            section.id === 'trash' ||
            isIncorrectPlatformType(subMenuConditionResult, mode)) {
            return;
        }
        var subMenuSlug = slugCreator(extractLocalizedString(section.title, locale));
        var subMenu = {
            id: section.id,
            title: section.title,
            displayableTitle: extractLocalizedString(section.title, locale),
            description: section.description,
            displayableDescription: extractLocalizedString(section.description, locale),
            sections: [],
            available: isAvailable(subMenuConditionResult),
            slug: subMenuSlug,
        };
        (section.children || []).forEach(function (menuSection) {
            var _a;
            var sectionConditionResult = evaluateHierarchyConditions({
                entity: menuSection,
                menuRoot: menu,
                conditionParams: conditionParams,
                itemsHash: itemsHash,
            });
            if (isHidden(sectionConditionResult) ||
                isIncorrectDispatchType(sectionConditionResult, mode) ||
                isIncorrectPlatformType(sectionConditionResult, mode)) {
                return;
            }
            if ((_a = menuSection.itemIds) === null || _a === void 0 ? void 0 : _a.length) {
                var sectionsSlug = slugCreator(extractLocalizedString(menuSection.title, locale));
                var displayableSection_1 = {
                    id: menuSection.id,
                    title: menuSection.title,
                    displayableTitle: extractLocalizedString(menuSection.title, locale),
                    description: menuSection.description,
                    displayableDescription: extractLocalizedString(menuSection.description, locale),
                    items: [],
                    available: isAvailable(sectionConditionResult),
                    image: extractImageUrl(menuSection),
                    slug: subMenuSlug + "/" + sectionsSlug,
                };
                menuSection.itemIds.forEach(function (itemId) {
                    var menuItem = itemsHash[itemId];
                    var id = menuItem.id, title = menuItem.title, description = menuItem.description, price = menuItem.price, labels = menuItem.labels, variations = menuItem.variations;
                    var itemConditionResult = evaluateHierarchyConditions({
                        entity: menuItem,
                        menuRoot: menu,
                        conditionParams: conditionParams,
                        sectionHint: menuSection,
                        itemsHash: itemsHash,
                    });
                    if (isHidden(itemConditionResult) ||
                        isIncorrectDispatchType(itemConditionResult, mode) ||
                        isIncorrectPlatformType(itemConditionResult, mode)) {
                        return;
                    }
                    var _a = calcMenuItemPriceRange(menu, menuItem.id), minPrice = _a.min, maxPrice = _a.maxPriceOfMandatoryChoices;
                    var _b = (variations || []).reduce(function (prev, variation) {
                        var _a = hasVariationSelectableChoices({
                            variation: variation,
                            dispatchTime: dispatchTime,
                            platform: platform,
                            dispatchType: dispatchType,
                            menu: menu,
                            itemsHash: itemsHash,
                        }), hasEnoughSelectableChoices = _a.hasEnoughSelectableChoices, reasons = _a.reasons;
                        var isChoiceSoldOut = Boolean(reasons === null || reasons === void 0 ? void 0 : reasons.includes(UnavailabileVariationReason.SoldOut));
                        return {
                            hasSoldoutVariationError: prev.hasSoldoutVariationError || isChoiceSoldOut,
                            hasUnavailableVariationError: prev.hasUnavailableVariationError ||
                                (!hasEnoughSelectableChoices && !isChoiceSoldOut),
                        };
                    }, {
                        hasSoldoutVariationError: false,
                        hasUnavailableVariationError: false,
                    }), hasSoldoutVariationError = _b.hasSoldoutVariationError, hasUnavailableVariationError = _b.hasUnavailableVariationError;
                    displayableSection_1.items.push({
                        id: id,
                        title: title,
                        displayableTitle: extractLocalizedString(title, locale),
                        description: description,
                        displayableDescription: extractLocalizedString(description, locale),
                        minPrice: minPrice,
                        price: price,
                        maxPrice: maxPrice,
                        displayableMinPrice: getDisplayablePrice(minPrice, locale, currency),
                        displayablePrice: price
                            ? getDisplayablePrice(price, locale, currency)
                            : '',
                        displayableMaxPrice: getDisplayablePrice(maxPrice, locale, currency),
                        image: extractImageUrl(menuItem, section.media),
                        labels: labels || [],
                        available: isAvailable(itemConditionResult) && !hasUnavailableVariationError,
                        soldout: isSoldOut(menuItem, dispatchTime) || hasSoldoutVariationError,
                        currency: currency,
                    });
                });
                if (displayableSection_1.items.length) {
                    subMenu.sections.push(displayableSection_1);
                }
            }
        });
        result.push(subMenu);
    });
    return __spreadArrays(result.filter(function (section) { return section.available; }), result.filter(function (section) { return !section.available; }));
}
function getIsHiddenForCatalog(_a) {
    var result = _a.result, reasons = _a.reasons;
    if (reasons.length === 1 && reasons[0].type === 'order_delivery_time') {
        // if the only reason to be hidden is the order delivery time, ignore it
        return false;
    }
    else {
        return !result;
    }
}
export function getCompactMenus(menu, locale, params) {
    return menu.sections
        .filter(function (section) { return section.id !== 'trash'; })
        .map(function (section) {
        var _a;
        var imageItem = (_a = section.children) === null || _a === void 0 ? void 0 : _a.flatMap(function (subSection) { var _a; return (_a = subSection.itemIds) === null || _a === void 0 ? void 0 : _a.map(function (dishId) { return menu.items.find(function (_a) {
            var id = _a.id;
            return dishId === id;
        }); }).filter(function (dish) { var _a; return (_a = dish === null || dish === void 0 ? void 0 : dish.media) === null || _a === void 0 ? void 0 : _a.logo; }); }).sort(function (a, b) {
            var _a, _b;
            if ((_a = a === null || a === void 0 ? void 0 : a.labels) === null || _a === void 0 ? void 0 : _a.includes('featured')) {
                return -1;
            }
            else if ((_b = b === null || b === void 0 ? void 0 : b.labels) === null || _b === void 0 ? void 0 : _b.includes('featured')) {
                return 1;
            }
            return ((b === null || b === void 0 ? void 0 : b.price) || 0) - ((a === null || a === void 0 ? void 0 : a.price) || 0);
        })[0];
        var conditionResult = evaluateCondition(section.displayCondition, params);
        return {
            id: section.id,
            title: extractLocalizedString(section.title, locale),
            sections: (section.children || []).map(function (menuSection) {
                var sectionConditionResult = evaluateCondition(menuSection.displayCondition, params);
                return {
                    id: menuSection.id,
                    title: extractLocalizedString(menuSection.title, locale),
                    isHidden: !sectionConditionResult.result,
                    isHiddenForOrders: !sectionConditionResult.result,
                    isHiddenForCatalog: getIsHiddenForCatalog(sectionConditionResult),
                };
            }),
            locationId: menu.locationId,
            image: imageItem ? extractImageUrl(imageItem) : undefined,
            isHidden: !conditionResult.result,
            isHiddenForOrders: !conditionResult.result,
            isHiddenForCatalog: getIsHiddenForCatalog(conditionResult),
        };
    });
}
function isSoldOut(item, dispatchTime) {
    var _a;
    var reasons = evaluateCondition(item.condition, { dispatchTime: dispatchTime }).reasons;
    var isExplicitlySoldOut = !!reasons.find(function (reason) {
        return reason.type === 'order_delivery_time' &&
            reason.reason === UnavailabilityReason.SoldOut;
    });
    var isOutOfStock = ((_a = item.stock) === null || _a === void 0 ? void 0 : _a.count) === 0;
    return isExplicitlySoldOut || isOutOfStock;
}
export function getDisplayableCatalog(catalog, locale, currency, dispatchTime, platform, dispatchType) {
    var menus = catalog.menus, items = catalog.items;
    var itemsHash = getCatalogItemsHash(items);
    var displayableMenus = [];
    var slugCreator = slugCreatorFactory();
    cachedDishChoicesMetadata.clear();
    for (var _i = 0, menus_1 = menus; _i < menus_1.length; _i++) {
        var menu = menus_1[_i];
        var displayableMenu = getCatalogMenu(catalog, menu, locale, currency, dispatchTime, platform, dispatchType, itemsHash, slugCreator);
        if (displayableMenu) {
            displayableMenus.push(displayableMenu);
        }
    }
    return __spreadArrays(displayableMenus.filter(function (menu) { return menu.available; }), displayableMenus.filter(function (menu) { return !menu.available; }));
}
function getCatalogMenu(catalog, menu, locale, currency, dispatchTime, platform, dispatchType, itemsHash, slugCreator) {
    var sections = catalog.sections;
    if (isHiddenCatalogItem(menu, dispatchType, platform)) {
        return;
    }
    var menuSections = getParentChilds(menu, sections, 'menuId');
    var displayableMenu = mapDisplayableMenu(menu, locale, dispatchTime, slugCreator);
    for (var _i = 0, menuSections_1 = menuSections; _i < menuSections_1.length; _i++) {
        var section = menuSections_1[_i];
        var displayableSection = getCatalogSection(catalog, displayableMenu, section, locale, currency, dispatchTime, platform, dispatchType, itemsHash, slugCreator, displayableMenu.slug);
        if (displayableSection === null || displayableSection === void 0 ? void 0 : displayableSection.items.length) {
            displayableMenu.sections.push(displayableSection);
        }
    }
    return displayableMenu;
}
function getCatalogSection(catalog, displayableMenu, section, locale, currency, dispatchTime, platform, dispatchType, itemsHash, slugCreator, menuSlug) {
    var _a, _b;
    if (isHiddenCatalogItem(section, dispatchType, platform)) {
        return;
    }
    if (!((_b = (_a = section.itemIds) === null || _a === void 0 ? void 0 : _a.values) === null || _b === void 0 ? void 0 : _b.length)) {
        return;
    }
    var displayableSection = mapDisplayableSection(section, displayableMenu, locale, dispatchTime, slugCreator, menuSlug);
    var sectionItems = section.itemIds.values;
    for (var _i = 0, sectionItems_1 = sectionItems; _i < sectionItems_1.length; _i++) {
        var itemId = sectionItems_1[_i];
        var displayableItem = getCatalogItem(catalog, displayableSection, itemId, locale, currency, dispatchTime, platform, dispatchType, itemsHash);
        if (displayableItem) {
            displayableSection.items.push(displayableItem);
        }
    }
    return displayableSection;
}
function getCatalogItem(catalog, displayableSection, itemId, locale, currency, dispatchTime, platform, dispatchType, itemsHash) {
    var item = itemsHash[itemId];
    if (!item || isHiddenCatalogItem(item, dispatchType, platform)) {
        return;
    }
    var _a = calcCatalogMenuItemPriceRange(catalog.items, itemId), min = _a.min, maxPriceOfMandatoryChoices = _a.maxPriceOfMandatoryChoices;
    return mapDisplayableItem(item, displayableSection, locale, currency, min, maxPriceOfMandatoryChoices, dispatchTime, platform, dispatchType, itemsHash);
}
export function isHiddenCatalogItem(item, dispatchType, platform) {
    var visible = (item.visibilityCriteria || {}).visible;
    return (!visible ||
        item.archived ||
        !isDisptachTypeAvailable(item, dispatchType) ||
        !isPlatformAvailable(item, platform));
}
export function isDisptachTypeAvailable(item, dispatchType) {
    var _a;
    var fulfillmentTypes = ((_a = item.visibilityCriteria) !== null && _a !== void 0 ? _a : {}).fulfillmentTypes;
    return ((dispatchType === 'delivery' && (fulfillmentTypes === null || fulfillmentTypes === void 0 ? void 0 : fulfillmentTypes.includes(FulfillmentType.DELIVERY))) ||
        (dispatchType === 'takeout' && (fulfillmentTypes === null || fulfillmentTypes === void 0 ? void 0 : fulfillmentTypes.includes(FulfillmentType.PICKUP_OR_DINE_IN))));
}
export function isPlatformAvailable(item, platform) {
    var platforms = (item.visibilityCriteria || {}).platforms;
    return platforms === null || platforms === void 0 ? void 0 : platforms.includes(platform);
}
function getParentChilds(parent, childs, childIdName) {
    return childs.filter(function (child) {
        return String(child[childIdName]) === parent.id;
    });
}
function mapDisplayableMenu(menu, locale, dispatchTime, slugCreator) {
    return __assign(__assign({}, mapItemCatalog(menu, undefined, locale, dispatchTime)), { sections: [], slug: slugCreator(getSafeStringValue(menu.name)) });
}
function mapDisplayableSection(section, parent, locale, dispatchTime, slugCreator, menuSlug) {
    var _a;
    return __assign(__assign({}, mapItemCatalog(section, parent, locale, dispatchTime)), { image: (_a = section.imageUrl) !== null && _a !== void 0 ? _a : undefined, items: [], slug: menuSlug + "/" + slugCreator(getSafeStringValue(section.name)) });
}
function mapDisplayableItem(item, parent, locale, currency, minPrice, maxPrice, dispatchTime, platform, dispatchType, itemsHash) {
    var _a, _b;
    var dishOptionsValidity = isDishOptionsValid({
        item: item,
        dispatchTime: dispatchTime,
        platform: platform,
        dispatchType: dispatchType,
        itemsHash: itemsHash,
    });
    var soldout = !dishOptionsValidity.isValid &&
        dishOptionsValidity.reasons.includes(UnavailabileVariationReason.SoldOut);
    var dishOptionsNotAvailable = !dishOptionsValidity.isValid && !soldout;
    var priceInCents = getPriceInCents(item.price);
    var outOfStock = item.inStock === false;
    return __assign(__assign({}, mapItemCatalog(item, parent, locale, dispatchTime, !outOfStock, !dishOptionsNotAvailable)), { minPrice: minPrice, price: priceInCents > 0 ? priceInCents : undefined, maxPrice: maxPrice, displayableMinPrice: getDisplayablePrice(minPrice, locale, currency), displayablePrice: priceInCents > 0
            ? getDisplayablePrice(Number(priceInCents), locale, currency)
            : '', displayableMaxPrice: getDisplayablePrice(maxPrice, locale, currency), image: (_a = item.imageUrl) !== null && _a !== void 0 ? _a : undefined, labels: ((_b = item.labels) === null || _b === void 0 ? void 0 : _b.values) || [], soldout: outOfStock || soldout, currency: currency });
}
function mapItemCatalog(item, parent, locale, dispatchTime, itemAvailable, dishOptionsValid) {
    var _a, _b;
    if (itemAvailable === void 0) { itemAvailable = true; }
    if (dishOptionsValid === void 0) { dishOptionsValid = true; }
    var name = getSafeStringValue(item.name);
    var description = getSafeStringValue(item.description);
    return {
        id: getSafeStringValue(item.id),
        title: (_a = {}, _a[locale] = name, _a),
        displayableTitle: name,
        description: (_b = {}, _b[locale] = description, _b),
        displayableDescription: description,
        available: (!parent || (parent === null || parent === void 0 ? void 0 : parent.available) === true) &&
            itemAvailable &&
            dishOptionsValid &&
            IsCatalogItamAvailable(item, dispatchTime),
    };
}
var ISO_DATE_FORMAT = 'YYYY-MM-DD';
export function IsCatalogItamAvailable(item, dispatchTime) {
    var _a;
    var availability = (_a = item.visibilityCriteria) === null || _a === void 0 ? void 0 : _a.availability;
    var timePeriods = (availability === null || availability === void 0 ? void 0 : availability.periods) || [];
    var specialHourPeriods = (availability === null || availability === void 0 ? void 0 : availability.specialHourPeriods) || [];
    var moment = require('moment-timezone');
    var tz = dispatchTime.tz();
    if (!tz) {
        dispatchTime.tz(moment.tz.guess());
    }
    // specialHourPeriod is prioritized over timePeriods
    for (var _i = 0, specialHourPeriods_1 = specialHourPeriods; _i < specialHourPeriods_1.length; _i++) {
        var specialHourPeriod = specialHourPeriods_1[_i];
        var startDate = moment(specialHourPeriod.startDate);
        var endDate = moment(specialHourPeriod.endDate);
        if (dispatchTime >= startDate && dispatchTime <= endDate) {
            return !!specialHourPeriod.available;
        }
    }
    var dayOfWeek = dispatchTime.isoWeekday();
    var timePeriod = timePeriods.find(function (t) {
        return t.openDay === days[dayOfWeek] ||
            // @ts-ignore
            (daysOfWeekAsWeekday[t.openDay] <= dayOfWeek &&
                // @ts-ignore
                daysOfWeekAsWeekday[t.closeDay]) >= dayOfWeek;
    });
    if (timePeriods.length > 0 && !timePeriod) {
        return false;
    }
    if (timePeriod) {
        var dispatchDate = dispatchTime.format(ISO_DATE_FORMAT).valueOf();
        var openTime = getDateWithTZ(moment, dispatchDate, timePeriod.openTime, dispatchTime.tz());
        var closeTime = getDateWithTZ(moment, dispatchDate, timePeriod.closeTime, dispatchTime.tz());
        if (dispatchTime > closeTime || dispatchTime < openTime) {
            return false;
        }
    }
    return true;
}
function getDateWithTZ(moment, date, time, tz) {
    var momentDate = moment.utc(getDateWithTimeString(date, time));
    var dateTimeWithTZ = tz ? momentDate.tz(tz) : momentDate;
    var timeOffset = dateTimeWithTZ === null || dateTimeWithTZ === void 0 ? void 0 : dateTimeWithTZ.utcOffset();
    return timeOffset
        ? moment(dateTimeWithTZ.add(-timeOffset, 'minutes'))
        : dateTimeWithTZ;
}
function getDateWithTimeString(date, time) {
    return date + ' ' + time;
}
/*
When a dish has an option with several choices that are also dishes on the menu (e.g. a "pizza+drink" combo where the pizzas and drinks are also available on their own):
Any choice that is currently out of stock, should appear on the option list as disabled.
If all choice of an option are out of stock and the option is mandatory - r.g. you have to select a drink but all drinks are sold out, we should show the entire dish (e.g. "pizza+drink") as out of stock, with all options within as disabled.
Same should apply if the "added" dish is hidden or otherwise unavailable.
*/
export function hasVariationSelectableChoices(_a) {
    var menu = _a.menu, variation = _a.variation, dispatchTime = _a.dispatchTime, platform = _a.platform, dispatchType = _a.dispatchType, itemsHash = _a.itemsHash;
    var choicesWithoutErrors = variation.itemIds.filter(function (itemId) {
        var item = itemsHash[itemId];
        var _a = getErrorsOfChoice({
            item: item,
            dispatchTime: dispatchTime,
            platform: platform,
            dispatchType: dispatchType,
            menu: menu,
            itemsHash: itemsHash,
        }), errors = _a.errors, isDishDeleted = _a.isDishDeleted;
        return errors.length === 0 && !isDishDeleted;
    });
    var min = variation.minNumAllowed || 0;
    var hasEnoughSelectableChoices = min <= choicesWithoutErrors.length;
    var reasons;
    if (!hasEnoughSelectableChoices) {
        var errors = variation.itemIds
            .map(function (itemId) {
            var item = itemsHash[itemId];
            var _a = getErrorsOfChoice({
                item: item,
                dispatchTime: dispatchTime,
                platform: platform,
                dispatchType: dispatchType,
                menu: menu,
                itemsHash: itemsHash,
            }), choiceErrors = _a.errors, isDishDeleted = _a.isDishDeleted;
            if (choiceErrors.length === 0 && !isDishDeleted) {
                return;
            }
            if (choiceErrors.find(function (err) {
                return err.type === 'order_delivery_time' && err.reason === 'soldout';
            })) {
                return UnavailabileVariationReason.SoldOut;
            }
            else if (choiceErrors.find(function (err) { return err.type === 'order_platform'; })) {
                return UnavailabileVariationReason.UnavailablePlatform;
            }
            else if (choiceErrors.find(function (err) { return err.type === 'order_delivery_type'; })) {
                return UnavailabileVariationReason.UnavailableDeliveryMethod;
            }
            else if (isDishDeleted ||
                choiceErrors.find(function (err) { return err.type === 'false'; })) {
                return UnavailabileVariationReason.Unavailable;
            }
            else {
                return UnavailabileVariationReason.UnavailableTime;
            }
        })
            .filter(function (err) { return err; });
        reasons = errors.find(function (err) { return err === UnavailabileVariationReason.SoldOut; })
            ? [UnavailabileVariationReason.SoldOut]
            : errors;
    }
    return { hasEnoughSelectableChoices: hasEnoughSelectableChoices, reasons: reasons };
}
export function isDishOptionsValid(_a) {
    var _b;
    var catalog = _a.catalog, item = _a.item, dispatchTime = _a.dispatchTime, platform = _a.platform, dispatchType = _a.dispatchType, itemsHash = _a.itemsHash;
    var isValid = true;
    var reasons = [];
    for (var _i = 0, _c = ((_b = item === null || item === void 0 ? void 0 : item.dishOptions) === null || _b === void 0 ? void 0 : _b.values) || []; _i < _c.length; _i++) {
        var dishOption = _c[_i];
        var dishOptionSelectableChoices = hasDishOptionSelectableChoices({
            catalog: catalog,
            dishOption: dishOption,
            dispatchTime: dispatchTime,
            platform: platform,
            dispatchType: dispatchType,
            itemsHash: itemsHash,
            depth: 0,
        });
        if (!dishOptionSelectableChoices.hasEnoughSelectableChoices) {
            if (dishOptionSelectableChoices.reasons) {
                reasons = __spreadArrays(reasons, dishOptionSelectableChoices.reasons);
            }
            isValid = false;
        }
    }
    return { isValid: isValid, reasons: reasons };
}
var cachedDishChoicesMetadata = new Map();
export function hasDishOptionSelectableChoices(_a) {
    var catalog = _a.catalog, dishOption = _a.dishOption, dispatchTime = _a.dispatchTime, platform = _a.platform, dispatchType = _a.dispatchType, itemsHash = _a.itemsHash, _b = _a.depth, depth = _b === void 0 ? 0 : _b;
    var _c = getChoicesMetadata({
        catalog: catalog,
        dishOption: dishOption,
        dispatchTime: dispatchTime,
        platform: platform,
        dispatchType: dispatchType,
        itemsHash: itemsHash,
        depth: depth,
    }), validChoices = _c.validChoices, reasons = _c.reasons;
    var minChoices = (dishOption
        ? getDishOptionChoicesRange(dishOption)
        : { min: 0 }).min;
    var hasEnoughSelectableChoices = minChoices <= validChoices.length;
    return { hasEnoughSelectableChoices: hasEnoughSelectableChoices, reasons: reasons };
}
function isChoiceValid(choice, itemsHash) {
    if (!choice.itemId) {
        return {
            reason: UnavailabileVariationReason.Unavailable,
            message: 'choice itemId is empty',
            isValid: false,
        };
    }
    var item = itemsHash[choice.itemId];
    if (!item) {
        return {
            reason: UnavailabileVariationReason.Unavailable,
            message: 'choice item not found by id',
            isValid: false,
        };
    }
    return { isValid: true, item: item };
}
var MAX_DEPTH_ALLOWED = 5;
function getChoicesMetadata(_a) {
    var _b, _c;
    var catalog = _a.catalog, dishOption = _a.dishOption, dispatchTime = _a.dispatchTime, platform = _a.platform, dispatchType = _a.dispatchType, itemsHash = _a.itemsHash, _d = _a.depth, depth = _d === void 0 ? 0 : _d;
    var choices = ((_b = dishOption === null || dishOption === void 0 ? void 0 : dishOption.selection) === null || _b === void 0 ? void 0 : _b.availableChoices) || ((_c = dishOption === null || dishOption === void 0 ? void 0 : dishOption.extras) === null || _c === void 0 ? void 0 : _c.availableChoices) ||
        [];
    var reasons = [];
    var validChoices = choices.filter(function (choice, index) {
        var _a, _b, _c;
        var choiceValidity = isChoiceValid(choice, itemsHash);
        if (!choiceValidity.isValid) {
            reasons.push((_a = choiceValidity.reason) !== null && _a !== void 0 ? _a : UnavailabileVariationReason.Unavailable);
            console.warn(choiceValidity.message, dishOption === null || dishOption === void 0 ? void 0 : dishOption.name, choice, index);
        }
        var item = choiceValidity.item;
        if (!item) {
            return;
        }
        var cacheKey = choice.itemId + "_" + dispatchType + "_" + dispatchTime;
        var choiseMetadata = cachedDishChoicesMetadata.get(cacheKey);
        if (choiseMetadata) {
            if (choiseMetadata.reason) {
                reasons.push(choiseMetadata.reason);
            }
            return choiseMetadata.isValid;
        }
        var itemAvailable = isItemAvailable(item, dispatchTime, platform, dispatchType);
        var isValid = itemAvailable.available;
        if (isValid && ((_b = item.dishOptions) === null || _b === void 0 ? void 0 : _b.values) && depth <= MAX_DEPTH_ALLOWED) {
            // dishOption is a ref to dish item with options
            for (var _i = 0, _d = item.dishOptions.values; _i < _d.length; _i++) {
                var innerDishOption = _d[_i];
                var selectableChoices = hasDishOptionSelectableChoices({
                    catalog: catalog,
                    dishOption: innerDishOption,
                    dispatchTime: dispatchTime,
                    platform: platform,
                    dispatchType: dispatchType,
                    itemsHash: itemsHash,
                    depth: depth + 1,
                });
                if (!selectableChoices.hasEnoughSelectableChoices) {
                    selectableChoices.reasons &&
                        reasons.concat(selectableChoices.reasons);
                    isValid = false;
                }
            }
        }
        if (isValid && catalog) {
            itemAvailable = isDishOptionHierarchyAvailable(catalog, item, dispatchTime, platform, dispatchType);
            isValid = itemAvailable.available;
        }
        if (!isValid) {
            var reason = (_c = itemAvailable.reason) !== null && _c !== void 0 ? _c : UnavailabileVariationReason.Unavailable;
            reasons.push(reason);
            cachedDishChoicesMetadata.set(cacheKey, { isValid: isValid, reason: reason });
        }
        else {
            cachedDishChoicesMetadata.set(cacheKey, { isValid: isValid });
        }
        return isValid;
    });
    return { validChoices: validChoices, reasons: reasons };
}
//# sourceMappingURL=menuLogic.js.map